import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL, resolveImg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  BookText, Users, MailQuestion, Star, Settings as SettingsIcon, LogOut,
  Plus, Pencil, Trash2, Upload, Download, GripVertical, Loader2, LayoutDashboard, ExternalLink,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const emptyBook = {
  title: "", subtitle: "", slug: "", category: "", description: "", long_description: "",
  cover_image: "", amazon_link: "", goodreads_link: "", isbn: "", publication_date: "",
  goodreads_rating: 0, goodreads_ratings_count: 0, featured: true, coming_soon: false,
  status: "published", meta_title: "", meta_description: "", order: 0,
};

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
        <Icon size={18} className="text-gold" />
      </div>
      <p className="font-display text-3xl font-bold mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const [analytics, setAnalytics] = useState(null);
  const [books, setBooks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [config, setConfig] = useState({});

  const [bookDialog, setBookDialog] = useState(false);
  const [editingBook, setEditingBook] = useState(emptyBook);
  const [uploading, setUploading] = useState(false);
  const [savingBook, setSavingBook] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const [tDialog, setTDialog] = useState(false);
  const [editingT, setEditingT] = useState({ name: "", text: "", rating: 5, source: "Goodreads", book_title: "", order: 0 });

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const loadAll = useCallback(() => {
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/books").then((r) => setBooks(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/config").then((r) => setConfig(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => { if (user && user.email) loadAll(); }, [user, loadAll]);

  if (!user || user === false || !user.email) {
    return <div className="min-h-screen grid place-items-center">Loading…</div>;
  }

  // ---- Books ----
  const openNewBook = () => { setEditingBook({ ...emptyBook, order: books.length }); setBookDialog(true); };
  const openEditBook = (b) => { setEditingBook({ ...b }); setBookDialog(true); };

  const saveBook = async () => {
    setSavingBook(true);
    try {
      const payload = { ...editingBook, goodreads_rating: Number(editingBook.goodreads_rating) || 0, goodreads_ratings_count: Number(editingBook.goodreads_ratings_count) || 0 };
      if (editingBook.id) await api.put(`/admin/books/${editingBook.id}`, payload);
      else await api.post("/admin/books", payload);
      toast.success("Book saved");
      setBookDialog(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSavingBook(false); }
  };

  const deleteBook = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    await api.delete(`/admin/books/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditingBook((b) => ({ ...b, cover_image: data.url }));
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = async (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const reordered = [...books];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, moved);
    setBooks(reordered);
    setDragIndex(null);
    await api.post("/admin/books/reorder", { ordered_ids: reordered.map((b) => b.id) });
    toast.success("Order updated");
  };

  // ---- Testimonials ----
  const saveT = async () => {
    try {
      const payload = { ...editingT, rating: Number(editingT.rating) || 5, order: Number(editingT.order) || 0 };
      if (editingT.id) await api.put(`/admin/testimonials/${editingT.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved");
      setTDialog(false);
      loadAll();
    } catch { toast.error("Save failed"); }
  };
  const deleteT = async (id) => { if (!window.confirm("Delete review?")) return; await api.delete(`/admin/testimonials/${id}`); loadAll(); };

  // ---- Config ----
  const saveConfig = async () => {
    try {
      await api.put("/admin/config", config);
      toast.success("Settings saved");
      loadAll();
    } catch { toast.error("Save failed"); }
  };
  const updateBadge = (i, v) => setConfig((c) => { const b = [...(c.trust_badges || [])]; b[i] = v; return { ...c, trust_badges: b }; });

  const exportCsv = () => {
    const token = localStorage.getItem("km_token");
    fetch(`${BACKEND_URL}/api/admin/newsletter/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "subscribers.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const field = (k, v) => setEditingBook((b) => ({ ...b, [k]: v }));

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "books", label: "Books", icon: BookText },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "subscribers", label: "Subscribers", icon: Users },
    { id: "enquiries", label: "Enquiries", icon: MailQuestion },
    { id: "settings", label: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F8] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0A0A0A] text-white p-6 sticky top-0 h-screen">
        <div className="mb-10">
          <p className="font-display text-xl font-bold">Kate Mercer</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">CMS</p>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === t.id ? "bg-gold text-black" : "text-gray-400 hover:bg-white/5 hover:text-white"}`}>
                <Icon size={18} /> {t.label}
              </button>
            );
          })}
        </nav>
        <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5">
          <LogOut size={18} /> Sign out
        </button>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Mobile tab bar */}
        <div className="md:hidden bg-[#0A0A0A] text-white px-4 py-3 flex gap-2 overflow-x-auto sticky top-0 z-20">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tab === t.id ? "bg-gold text-black" : "bg-white/10"}`}>{t.label}</button>
          ))}
        </div>

        <div className="p-5 sm:p-10 max-w-6xl">
          {/* OVERVIEW */}
          {tab === "overview" && analytics && (
            <div data-testid="admin-overview">
              <h1 className="font-display text-3xl font-bold mb-8">Overview</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat icon={Users} label="Subscribers" value={analytics.total_subscribers} />
                <Stat icon={MailQuestion} label="Enquiries" value={analytics.total_enquiries} />
                <Stat icon={BookText} label="Published Books" value={analytics.published_books} />
                <Stat icon={BookText} label="Total Books" value={analytics.total_books} />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <p className="font-semibold mb-4">Activity (last 14 days)</p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="subscribers" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="enquiries" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* BOOKS */}
          {tab === "books" && (
            <div data-testid="admin-books">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-display text-3xl font-bold">Books</h1>
                  <p className="text-sm text-gray-500 mt-1">Drag rows to reorder. Add unlimited books.</p>
                </div>
                <button onClick={openNewBook} data-testid="admin-add-book" className="btn-gold rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                  <Plus size={16} /> Add Book
                </button>
              </div>
              <div className="space-y-2">
                {books.map((b, i) => (
                  <div key={b.id} draggable onDragStart={() => setDragIndex(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                       className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-4" data-testid={`admin-book-row-${b.slug}`}>
                    <GripVertical size={18} className="text-gray-300 cursor-grab shrink-0" />
                    <div className="w-10 h-14 rounded bg-gray-100 overflow-hidden shrink-0">
                      {b.cover_image && <img src={resolveImg(b.cover_image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{b.title}</p>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.status === "published" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>{b.status}</span>
                        {b.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-gold">featured</span>}
                        {b.coming_soon && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">coming soon</span>}
                      </div>
                    </div>
                    <button onClick={() => openEditBook(b)} data-testid={`admin-edit-${b.slug}`} className="p-2 hover:bg-gray-100 rounded-lg"><Pencil size={16} /></button>
                    <button onClick={() => deleteBook(b.id)} data-testid={`admin-delete-${b.slug}`} className="p-2 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* REVIEWS */}
          {tab === "reviews" && (
            <div data-testid="admin-reviews">
              <div className="flex items-center justify-between mb-8">
                <h1 className="font-display text-3xl font-bold">Reader Reviews</h1>
                <button onClick={() => { setEditingT({ name: "", text: "", rating: 5, source: "Goodreads", book_title: "", order: testimonials.length }); setTDialog(true); }}
                        data-testid="admin-add-review" className="btn-gold rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                  <Plus size={16} /> Add Review
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`admin-review-${t.id}`}>
                    <div className="flex justify-between items-start">
                      <div><p className="font-medium">{t.name}</p><p className="text-xs text-gray-500">{t.book_title} · {t.source} · {t.rating}★</p></div>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditingT(t); setTDialog(true); }} className="p-1.5 hover:bg-gray-100 rounded"><Pencil size={14} /></button>
                        <button onClick={() => deleteT(t.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">"{t.text}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SUBSCRIBERS */}
          {tab === "subscribers" && (
            <div data-testid="admin-subscribers">
              <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-3xl font-bold">Subscribers</h1><p className="text-sm text-gray-500 mt-1">{subs.length} total</p></div>
                <button onClick={exportCsv} data-testid="admin-export-csv" className="btn-ink rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Download size={16} /> Export CSV</button>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                {subs.length === 0 && <p className="p-6 text-sm text-gray-500">No subscribers yet.</p>}
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-5 py-3 border-b border-gray-100 last:border-0">
                    <div><p className="text-sm font-medium">{s.email}</p>{s.name && <p className="text-xs text-gray-500">{s.name}</p>}</div>
                    <span className="text-xs text-gray-400">{(s.created_at || "").slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ENQUIRIES */}
          {tab === "enquiries" && (
            <div data-testid="admin-enquiries">
              <h1 className="font-display text-3xl font-bold mb-8">Enquiries</h1>
              <div className="space-y-3">
                {contacts.length === 0 && <p className="text-sm text-gray-500">No enquiries yet.</p>}
                {contacts.map((c) => (
                  <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex justify-between items-start">
                      <div><p className="font-medium">{c.name} <span className="text-xs text-gray-400 font-normal">· {c.email}</span></p><p className="text-xs text-gold mt-0.5">{c.subject}</p></div>
                      <span className="text-xs text-gray-400">{(c.created_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{c.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SETTINGS */}
          {tab === "settings" && (
            <div data-testid="admin-settings" className="max-w-2xl">
              <h1 className="font-display text-3xl font-bold mb-8">Settings</h1>
              <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
                <p className="text-xs uppercase tracking-wider text-gold font-semibold">Homepage</p>
                <div><Label>Hero headline</Label><Input value={config.hero_headline || ""} onChange={(e) => setConfig({ ...config, hero_headline: e.target.value })} /></div>
                <div><Label>Hero subheading</Label><Textarea value={config.hero_subheading || ""} onChange={(e) => setConfig({ ...config, hero_subheading: e.target.value })} /></div>

                <p className="text-xs uppercase tracking-wider text-gold font-semibold pt-4 border-t border-gray-100">Biography</p>
                <div><Label>About title</Label><Input value={config.about_title || ""} onChange={(e) => setConfig({ ...config, about_title: e.target.value })} data-testid="admin-about-title" /></div>
                <div>
                  <Label>Biography (separate each paragraph with a blank line)</Label>
                  <Textarea rows={10} value={(config.about_paragraphs || []).join("\n\n")}
                            onChange={(e) => setConfig({ ...config, about_paragraphs: e.target.value.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean) })}
                            data-testid="admin-about-bio" />
                </div>
                <div><Label>Mission statement</Label><Textarea value={config.about_mission || ""} onChange={(e) => setConfig({ ...config, about_mission: e.target.value })} data-testid="admin-about-mission" /></div>

                <p className="text-xs uppercase tracking-wider text-gold font-semibold pt-4 border-t border-gray-100">Links &amp; Social</p>
                <div><Label>Goodreads author / reviews URL</Label><Input value={config.goodreads_author_url || ""} onChange={(e) => setConfig({ ...config, goodreads_author_url: e.target.value })} /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Instagram URL</Label><Input value={config.instagram_url || ""} onChange={(e) => setConfig({ ...config, instagram_url: e.target.value })} /></div>
                  <div><Label>Instagram handle</Label><Input value={config.instagram_handle || ""} onChange={(e) => setConfig({ ...config, instagram_handle: e.target.value })} /></div>
                  <div><Label>TikTok URL</Label><Input value={config.tiktok_url || ""} onChange={(e) => setConfig({ ...config, tiktok_url: e.target.value })} /></div>
                  <div><Label>TikTok handle</Label><Input value={config.tiktok_handle || ""} onChange={(e) => setConfig({ ...config, tiktok_handle: e.target.value })} /></div>
                </div>
                <div><Label>Contact email (enquiries delivered here)</Label><Input value={config.contact_email || ""} onChange={(e) => setConfig({ ...config, contact_email: e.target.value })} /></div>
                <div>
                  <Label>Trust badges</Label>
                  <div className="space-y-2 mt-1">
                    {(config.trust_badges || []).map((b, i) => (
                      <Input key={i} value={b} onChange={(e) => updateBadge(i, e.target.value)} />
                    ))}
                  </div>
                </div>
                <button onClick={saveConfig} data-testid="admin-save-config" className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold">Save Settings</button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Book Dialog */}
      <Dialog open={bookDialog} onOpenChange={setBookDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingBook.id ? "Edit Book" : "Add Book"}</DialogTitle><DialogDescription>Manage cover, details, links, SEO and publish status.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <div className="w-24 h-32 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                {editingBook.cover_image && <img src={resolveImg(editingBook.cover_image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <Label>Cover image</Label>
                <label className="mt-1 flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm cursor-pointer hover:bg-gray-50" data-testid="admin-upload-cover">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? "Uploading…" : "Upload cover"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files[0])} />
                </label>
                <Input className="mt-2" placeholder="…or paste image URL" value={editingBook.cover_image || ""} onChange={(e) => field("cover_image", e.target.value)} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={editingBook.title} onChange={(e) => field("title", e.target.value)} data-testid="book-form-title" /></div>
              <div><Label>Subtitle</Label><Input value={editingBook.subtitle} onChange={(e) => field("subtitle", e.target.value)} /></div>
              <div><Label>Slug (auto if blank)</Label><Input value={editingBook.slug} onChange={(e) => field("slug", e.target.value)} /></div>
              <div><Label>Category</Label><Input value={editingBook.category} onChange={(e) => field("category", e.target.value)} /></div>
            </div>
            <div><Label>Short description</Label><Textarea value={editingBook.description} onChange={(e) => field("description", e.target.value)} /></div>
            <div><Label>Long description</Label><Textarea value={editingBook.long_description} onChange={(e) => field("long_description", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Amazon link</Label><Input value={editingBook.amazon_link} onChange={(e) => field("amazon_link", e.target.value)} /></div>
              <div><Label>Goodreads link</Label><Input value={editingBook.goodreads_link} onChange={(e) => field("goodreads_link", e.target.value)} /></div>
              <div><Label>ISBN</Label><Input value={editingBook.isbn} onChange={(e) => field("isbn", e.target.value)} /></div>
              <div><Label>Publication date</Label><Input value={editingBook.publication_date} onChange={(e) => field("publication_date", e.target.value)} /></div>
              <div><Label>Goodreads rating</Label><Input type="number" step="0.1" value={editingBook.goodreads_rating} onChange={(e) => field("goodreads_rating", e.target.value)} /></div>
              <div><Label>Ratings count</Label><Input type="number" value={editingBook.goodreads_ratings_count} onChange={(e) => field("goodreads_ratings_count", e.target.value)} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Meta title (SEO)</Label><Input value={editingBook.meta_title} onChange={(e) => field("meta_title", e.target.value)} /></div>
              <div><Label>Meta description (SEO)</Label><Input value={editingBook.meta_description} onChange={(e) => field("meta_description", e.target.value)} /></div>
            </div>
            <div className="flex flex-wrap gap-6 pt-2">
              <div className="flex items-center gap-2"><Switch checked={editingBook.status === "published"} onCheckedChange={(v) => field("status", v ? "published" : "draft")} data-testid="book-form-publish" /><Label>Published</Label></div>
              <div className="flex items-center gap-2"><Switch checked={editingBook.featured} onCheckedChange={(v) => field("featured", v)} /><Label>Featured</Label></div>
              <div className="flex items-center gap-2"><Switch checked={editingBook.coming_soon} onCheckedChange={(v) => field("coming_soon", v)} /><Label>Coming soon</Label></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setBookDialog(false)} className="rounded-full px-5 py-2.5 text-sm border border-gray-300">Cancel</button>
            <button onClick={saveBook} disabled={savingBook} data-testid="book-form-save" className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              {savingBook && <Loader2 size={16} className="animate-spin" />} Save Book
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Testimonial Dialog */}
      <Dialog open={tDialog} onOpenChange={setTDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingT.id ? "Edit Review" : "Add Review"}</DialogTitle><DialogDescription>Add or edit a reader review shown on the site.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Reader name</Label><Input value={editingT.name} onChange={(e) => setEditingT({ ...editingT, name: e.target.value })} data-testid="review-form-name" /></div>
            <div><Label>Review text</Label><Textarea value={editingT.text} onChange={(e) => setEditingT({ ...editingT, text: e.target.value })} data-testid="review-form-text" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Rating</Label><Input type="number" min="1" max="5" value={editingT.rating} onChange={(e) => setEditingT({ ...editingT, rating: e.target.value })} /></div>
              <div><Label>Source</Label><Input value={editingT.source} onChange={(e) => setEditingT({ ...editingT, source: e.target.value })} /></div>
              <div><Label>Book</Label><Input value={editingT.book_title} onChange={(e) => setEditingT({ ...editingT, book_title: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setTDialog(false)} className="rounded-full px-5 py-2.5 text-sm border border-gray-300">Cancel</button>
            <button onClick={saveT} data-testid="review-form-save" className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <a href="/" target="_blank" rel="noreferrer" className="fixed bottom-5 right-5 btn-ink rounded-full px-4 py-2.5 text-xs font-semibold inline-flex items-center gap-2 shadow-lg z-30" data-testid="admin-view-site">
        <ExternalLink size={14} /> View site
      </a>
    </div>
  );
}
